/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useState, useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { SimpleList, Button, Checkbox, HierarchyList, ComboBox } from 'carbon-addons-iot-react';
import { Subtract16 } from '@carbon/icons-react';
import isEmpty from 'lodash/isEmpty';
import hash from 'object-hash';
import sortBy from 'lodash/sortBy';
import noop from 'lodash/noop';
import cloneDeep from 'lodash/cloneDeep';

import BaseModal from '../../BaseModal/BaseModal';
import { messages as comboBoxMessages } from '../../ComboBoxFormik/ComboBoxFormik';

import { AvailableColumnsType, ViewColumnType, SelectableViewColumnType } from 'TableTypes';

const messages = defineMessages({
  defaultColumns: 'Default columns',
  availableColumns: 'Available columns',
  tableColumns: 'Table columns',
  helpText: 'Select the columns to display on the table. Drag columns to reorder them.',
  dialogTitle: 'Customize columns',
  cancel: 'Cancel',
  removeColumn: 'Remove {columnName}',
  save: 'Save',
  selectColumn: 'Select {columnName}',
  drillDownComboBoxTitle: 'Link to device from this column',
  drillDownComboBoxPlaceholder: 'Select',
});

type CustomizeColumnsModalType = {
  columns?: ViewColumnType[];
  /** full list of column names and ids */
  availableColumns: SelectableViewColumnType[];
  onSubmit?: (customizedColumns: ViewColumnType[]) => void;
  onClose?: () => void;
  isOpen?: boolean;
  customColumnsText: string;
};

const defaultProps = {
  columns: [],
  onSubmit: noop,
  onClose: noop,
  isOpen: true,
};

export const getDrilldownColumns = (
  availableColumns: SelectableViewColumnType[],
  visibleDrillDownColumns: ViewColumnType[]
): { id: string; text: string }[] =>
  availableColumns
    .filter(defaultColumn =>
      visibleDrillDownColumns.some(
        visibleDrillDownColumn => visibleDrillDownColumn.id === defaultColumn.id
      )
    )
    .map(({ id, columnName }) => ({
      id,
      text: columnName || id,
    }));

export const separateColumnsIntoBuckets = (
  availableColumns: SelectableViewColumnType[]
): AvailableColumnsType =>
  availableColumns.reduce(
    (columnsAcc: AvailableColumnsType, column) => ({
      defaultColumns: column.isDefault
        ? [...columnsAcc.defaultColumns, column]
        : columnsAcc.defaultColumns,
      customColumns: !column.isDefault
        ? [...columnsAcc.customColumns, column]
        : columnsAcc.customColumns,
    }),
    { defaultColumns: [], customColumns: [] }
  );

const CustomizeColumnsModal = ({
  columns = [],
  availableColumns = [],
  onSubmit = noop,
  onClose = noop,
  isOpen = true,
  customColumnsText,
}: CustomizeColumnsModalType): JSX.Element => {
  const { formatMessage } = useIntl();
  // localColumnsState will only contain visible columns in it
  const [localColumnsState, setLocalColumnsState] = useState(columns);

  // Need to separate the default alert columns from the custom ones
  const availableColumnsInBuckets = separateColumnsIntoBuckets(availableColumns);

  const selectedColumns = useMemo(
    () =>
      localColumnsState.map(({ id, columnName }) => ({
        id,
        content: {
          value: columnName || id,
          rowActions: (): JSX.Element[] => [
            <Button
              key={`${columnName}-edit`}
              title={formatMessage(messages.removeColumn, { columnName })}
              kind="ghost"
              renderIcon={Subtract16}
              size="small"
              hasIconOnly
              onClick={(): void => {
                const updatedColumns = cloneDeep(localColumnsState);
                // remove the clicked column
                updatedColumns.splice(
                  updatedColumns.findIndex(column => column.id === id),
                  1
                );
                setLocalColumnsState(updatedColumns);
              }}
            />,
          ],
        },
        isSelectable: true,
      })),
    [localColumnsState, formatMessage]
  );

  //  get the drill down column name on initial load and set it.
  // only custom columns should show in the drilldown links

  const [drilldownColumn, setDrilldownColumn] = useState(() => {
    const visibleDrillDownColumns = columns.filter(
      ({ hasDrilldown }) => hasDrilldown // Use hasDrilldown and non default column to get the dimension drilldown column
    );

    const customDrillDownColumns = getDrilldownColumns(
      availableColumnsInBuckets.customColumns,
      visibleDrillDownColumns
    );
    return isEmpty(customDrillDownColumns)
      ? undefined // if no custom and no default dril down do not select anything
      : {
          // if custom dril down column present then use it
          id: customDrillDownColumns[0].id,
          text: customDrillDownColumns[0].text,
        };
  });

  // Only the custom columns should be selectable as drilldown links
  const availableDrilldownColumns = useMemo(
    () =>
      localColumnsState
        .filter(
          visibleColumn =>
            availableColumnsInBuckets.customColumns?.find(
              customColumn => customColumn.id === visibleColumn.id
            )
        )
        .map(({ id, columnName }) => ({
          id,
          text: columnName || id,
        })),
    [availableColumnsInBuckets.customColumns, localColumnsState]
  );

  return (
    <BaseModal
      isOpen={isOpen}
      className="customize-columns-modal"
      header={{
        helpText: formatMessage(messages.helpText),
        title: formatMessage(messages.dialogTitle),
      }}
      onClose={onClose}
      footer={{
        primaryButtonText: formatMessage(messages.save),
        secondaryButtonText: formatMessage(messages.cancel),
      }}
      onSubmit={(): void => {
        onSubmit(localColumnsState);
      }}>
      <div className="customize-columns-modal--container">
        <div className="customize-columns-modal--container--left">
          <HierarchyList
            className="customize-columns-modal--container--list"
            title={formatMessage(messages.availableColumns)}
            items={Object.keys(availableColumnsInBuckets)
              .filter(columnType => !isEmpty(availableColumnsInBuckets[columnType]))
              .map(columnType => ({
                content: {
                  value:
                    columnType === 'customColumns'
                      ? customColumnsText
                      : formatMessage(messages[columnType]),
                },
                id: `columns-list-section--${columnType}`,
                isCategory: true,
                isSelectable: false,
                children: sortBy(availableColumnsInBuckets[columnType], ({ id }) => {
                  if (localColumnsState.find(column => column.id === id)) {
                    return `1-${id}`; // this will force selected columns to the top of the list
                  }
                  return `2-${id}`;
                }).map(({ id, columnName }) => ({
                  id,
                  isSelectable: false,
                  content: {
                    value: columnName,
                    icon: (
                      <Checkbox
                        id={`columns-list-checkbox--${id}`}
                        name={`columns-list-checkbox--${id}`}
                        checked={!!localColumnsState.find(column => column.id === id)}
                        onChange={(checked): void => {
                          const updatedColumns = cloneDeep(localColumnsState);
                          if (checked) {
                            updatedColumns.push({
                              id,
                              columnName,
                              isVisible: true,
                              hasDrilldown: false,
                            });
                          } else {
                            updatedColumns.splice(
                              updatedColumns.findIndex(column => column.id === id),
                              1
                            );
                          }

                          setLocalColumnsState(updatedColumns);
                        }}
                        labelText={formatMessage(messages.selectColumn, { columnName })}
                        hideLabel
                      />
                    ),
                  },
                })),
              }))}
            hasSearch
            hasPagination={false}
            onSelect={noop}
            onExpandedChange={noop}
          />
        </div>
        <div className="customize-columns-modal--container--right">
          <div className="iot--list-header">
            <div className="iot--list-header--title">{formatMessage(messages.tableColumns)}</div>
          </div>

          <ComboBox
            light
            title={formatMessage(messages.drillDownComboBoxTitle)}
            translateWithId={(id): React.ReactNode => formatMessage(comboBoxMessages[id])}
            id={formatMessage(messages.drillDownComboBoxTitle)}
            key={JSON.stringify(localColumnsState)}
            titleText={formatMessage(messages.drillDownComboBoxTitle)}
            placeholder={formatMessage(messages.drillDownComboBoxPlaceholder)}
            itemToString={(item): string => item?.text}
            items={availableDrilldownColumns}
            onChange={(selectedItem): void => {
              if (selectedItem?.text) {
                let updatedColumns = cloneDeep(localColumnsState);

                // remove hasDrilldown link for all the custom column items
                updatedColumns = updatedColumns.map(
                  column =>
                    availableColumnsInBuckets.defaultColumns.some(defaultColumn =>
                      defaultColumn.columnName.includes(column.columnName)
                    ) // if present in table defult columns list then dont change the configuration
                      ? column
                      : { ...column, hasDrilldown: false } // should be custom column, so reset the hasDrilldown to false
                );

                const currentColumnIndex = updatedColumns.findIndex(
                  column => column.id === selectedItem.id || column.columnName === selectedItem.id
                );
                updatedColumns[currentColumnIndex].hasDrilldown = true;
                setLocalColumnsState(updatedColumns);

                setDrilldownColumn(selectedItem);
              }
            }}
            initialSelectedItem={drilldownColumn}
          />
          <SimpleList
            key={hash(localColumnsState)} // need to listen for changes on the localColumnState
            className="customize-columns-modal--container--list"
            title=""
            hasSearch
            hasPagination={false}
            editingStyle="single"
            onListUpdated={(
              newColumnOrder // Event for drag and drop reorder
            ): void => {
              setLocalColumnsState([
                ...newColumnOrder.map(({ id }) => ({
                  // then put in the visible columns in the right order
                  ...localColumnsState.find(({ id: columnId }) => columnId === id), // then put in the updated columns
                })),
              ]);
            }}
            items={selectedColumns}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default CustomizeColumnsModal;
CustomizeColumnsModal.defaultProps = defaultProps;
